import React, { FunctionComponent, useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import style from './equipmentSearchFilter.css'
import { GlobalEquipmentSearchResult } from '../../entities/GlobalEquipmentSearchResult'
import urlifyGroupName from '../../helpers/urlifyGroupName'
import PageSearchInput from './page-search-input'
import GlobalSearchInput from './global-search-input'
import useTranslation from '../../hooks/useTranslation'

export type Props = {
    setFilter: (value: string) => void
    value: string
    equipmentGlobalSearchResults?: GlobalEquipmentSearchResult[]
    setEquipmentGlobalSearchQuery?: (query: string) => void
    globalSearchQuery?: string
    searchMode: 'page' | 'global'
    setSearchMode: (mode: 'page' | 'global') => void
}

export type SearchMode = 'page' | 'global'

const EquipmentSearchFilter: FunctionComponent<Props> = (props) => {
    const {
        setFilter,
        value,
        equipmentGlobalSearchResults = [],
        setEquipmentGlobalSearchQuery,
        globalSearchQuery = '',
        searchMode,
        setSearchMode,
    } = props
    const history = useHistory()
    const { t } = useTranslation()
    const currentPath = history.location.pathname

    const [pageSearchValue, setPageSearchValue] = useState(value)
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const [previousSearchMode, setPreviousSearchMode] = useState<SearchMode>(searchMode)
    const userInteracted = useRef<boolean>(false)

    const currentSearchValue = searchMode === 'global' ? globalSearchQuery : pageSearchValue

    const updateSearchMode = (newMode: SearchMode) => {
        if (newMode === 'page' && searchMode === 'global' && globalSearchQuery) {
            setPageSearchValue(globalSearchQuery)
            setFilter(globalSearchQuery)
        }
        else if (newMode === 'global' && searchMode === 'page') {
            if (setEquipmentGlobalSearchQuery) {
                setEquipmentGlobalSearchQuery(pageSearchValue)
            }
        }
        setSearchMode(newMode)
    }

    const handleToggleClick = () => {
        const newMode = searchMode === 'global' ? 'page' : 'global'

        if (newMode === 'global') {
            setFilter('')
            if (globalSearchQuery && setEquipmentGlobalSearchQuery) {
                setEquipmentGlobalSearchQuery(globalSearchQuery)
            }
        }

        updateSearchMode(newMode)
    }

    useEffect(() => {
        setPageSearchValue('')
        setFilter('')
        userInteracted.current = false
    }, [currentPath, setFilter])

    const handleGlobalResultSelect = (result: GlobalEquipmentSearchResult) => {
        const resultPath = urlifyGroupName(result.equipmentGroupId)
        const hash = result.equipmentId
        const currentPath = history.location.pathname

        const isCurrentPage = currentPath.includes(resultPath) || currentPath === `/${resultPath}`

        if (isCurrentPage) {
            const element = document.getElementById(hash)
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' })
            } else {
                window.location.hash = hash
            }
        } else {
            history.push(`${resultPath}#${encodeURIComponent(hash)}`)
        }
    }

    const globalDropdownOptions = searchMode === 'global' && globalSearchQuery.length >= 2 ? equipmentGlobalSearchResults : []

    useEffect(() => {
        const modeChanged = previousSearchMode !== searchMode
        const isGlobalMode = searchMode === 'global'
        const isQueryValid = globalSearchQuery.length >= 2
        const shouldShowGlobalResults = isGlobalMode && isQueryValid

        if (shouldShowGlobalResults) {
            setEquipmentGlobalSearchQuery?.(globalSearchQuery)

            if (modeChanged || userInteracted.current) {
                setIsDropdownOpen(true)
            }
        } else {
            setIsDropdownOpen(false)
        }

        if (!isGlobalMode) {
            setPageSearchValue(value)
        }

        setPreviousSearchMode(searchMode)
    }, [searchMode, value, globalSearchQuery, previousSearchMode, setEquipmentGlobalSearchQuery])

    const renderSearchInput = () => {
        if (searchMode === 'page') {
            return (
                <PageSearchInput
                    value={currentSearchValue}
                    onChange={(newValue) => {
                        setPageSearchValue(newValue)
                        setFilter(newValue)
                    }}
                    placeholder={t('equipmentGlobalSearch.inputPlaceholder')}
                />
            )
        } else {
            return (
                <GlobalSearchInput
                    value={currentSearchValue}
                    onChange={(newValue) => {
                        userInteracted.current = true
                        if (setEquipmentGlobalSearchQuery) {
                            setEquipmentGlobalSearchQuery(newValue)
                        }
                    }}
                    onSelect={handleGlobalResultSelect}
                    results={globalDropdownOptions}
                    isOpen={isDropdownOpen}
                    onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
                    placeholder={t('equipmentGlobalSearch.inputPlaceholder')}
                />
            )
        }
    }

    return (
        <div className={style.container}>
            <div className={style.header}>
                <div className={style.toggleRow}>
                    <div
                        className={`${style.switch} ${searchMode === 'global' ? style.switchActive : ''}`}
                        onClick={handleToggleClick}
                        data-testid="search-mode-switch"
                    >
                        <div className={style.switchHandle} data-testid="search-mode-switch-handle"></div>
                    </div>
                    <span className={`${style.toggleLabel} ${searchMode === 'global' ? style.toggleLabelActive : style.toggleLabelInactive}`}>{t('equipmentGlobalSearch.toggleLabel')}</span>
                </div>
            </div>

            <div className={style.searchWrapper}>
                {renderSearchInput()}
            </div>

        </div>
    )
}

export default EquipmentSearchFilter
